import React, { useState } from 'react';
import { Trophy, BarChart3, Clock, Users } from 'lucide-react';

const Sidebar = ({ 
  mode, 
  tournamentData, 
  getTeamName, 
  allPlayers, 
  bench, 
  nextPlayersInfo, 
  rotationManager, 
  competitionLevel,
  onPlayerLeave,
  onPlayerReplace,
  onAddPlayer,
  maxPlayers
}) => {
  const [expandedSections, setExpandedSections] = useState({
    progress: true,
    standings: true,
    remaining: true,
    bench: true,
    nextUp: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (mode === 'mlp') {
    const { progress, standings, remainingGames, waitingPlayers } = tournamentData;

    return (
      <div className="sidebar">
        {/* Tournament Progress */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => toggleSection('progress')}
            style={{ cursor: 'pointer' }}
          >
            <Trophy size={20} color="#deff01" />
            <span>Tournament Progress</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
              {expandedSections.progress ? '▲' : '▼'}
            </span>
          </div>
          
          {expandedSections.progress && (
            <div className="section-content">
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                  Games Complete:
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#deff01' }}>
                  {progress.completedGames}/24
                </div>
                <div style={{ fontSize: '14px', color: '#deff01', marginTop: '4px' }}>
                  {progress.progressPercentage}% Complete
                </div>
              </div>
              
              {/* Progress Bar */}
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: `${progress.progressPercentage}%`,
                  height: '100%',
                  backgroundColor: '#deff01',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
          )}
        </div>

        {/* Tournament Standings */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => toggleSection('standings')}
            style={{ cursor: 'pointer' }}
          >
            <BarChart3 size={20} color="#deff01" />
            <span>Team Standings</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
              {expandedSections.standings ? '▲' : '▼'}
            </span>
          </div>
          
          {expandedSections.standings && (
            <div className="section-content">
              <div className="standings-header">
                <span style={{ flex: '0.5' }}>Rank</span>
                <span style={{ flex: '2' }}>Team</span>
                <span style={{ flex: '1' }}>W-L</span>
                <span style={{ flex: '1' }}>%</span>
              </div>
              
              {standings.map((team, index) => (
                <div key={team.team} className="standings-row">
                  <span className="rank-badge">#{index + 1}</span>
                  <span style={{ flex: '2', fontWeight: '500' }}>{team.teamName}</span>
                  <span style={{ flex: '1' }}>{team.wins}-{team.losses}</span>
                  <span style={{ flex: '1' }}>{team.winPercentage}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Remaining Games */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => toggleSection('remaining')}
            style={{ cursor: 'pointer' }}
          >
            <Clock size={20} color="#deff01" />
            <span>Remaining Games ({remainingGames.total})</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
              {expandedSections.remaining ? '▲' : '▼'}
            </span>
          </div>
          
          {expandedSections.remaining && (
            <div className="section-content">
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
                {remainingGames.completed} completed • {remainingGames.total} remaining
              </div>
              
              {Object.entries(remainingGames.breakdown).map(([gameType, data]) => (
                <div key={gameType} className="game-type-dropdown">
                  <div className="dropdown-header">
                    <span>{gameType === 'MM' ? "Men's" : gameType === 'FF' ? "Women's" : gameType === 'MX1' ? 'Mixed 1' : 'Mixed 2'}</span>
                    <span>({data.count} left)</span>
                    <span style={{ marginLeft: 'auto' }}>▼</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bench */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => toggleSection('bench')}
            style={{ cursor: 'pointer' }}
          >
            <Users size={20} color="#deff01" />
            <span>Bench ({waitingPlayers ? waitingPlayers.length : 4} waiting)</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
              {expandedSections.bench ? '▲' : '▼'}
            </span>
          </div>
          
          {expandedSections.bench && (
            <div className="section-content">
              <div className="bench-grid">
                {waitingPlayers && waitingPlayers.length > 0 ? (
                  waitingPlayers.map((player, index) => (
                    <div key={index} className="bench-player">
                      {player}
                    </div>
                  ))
                ) : (
                  ['Lauren', 'Megan', 'Nicole', 'Samantha'].map((player, index) => (
                    <div key={index} className="bench-player">
                      {player}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // SMART mode sidebar - Complete implementation
  return (
    <div className="sidebar">
      {/* Next Up Section */}
      <div className="sidebar-section">
        <div 
          className="sidebar-header"
          onClick={() => toggleSection('nextUp')}
          style={{ cursor: 'pointer' }}
        >
          <Users size={20} color="#deff01" />
          <span>Next Up</span>
          <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
            {expandedSections.nextUp ? '▲' : '▼'}
          </span>
        </div>
        
        {expandedSections.nextUp && (
          <div className="section-content">
            {nextPlayersInfo && nextPlayersInfo.players && nextPlayersInfo.players.length > 0 ? (
              <div>
                <div style={{ marginBottom: '12px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                  Ready for Court 1:
                </div>
                <div className="next-players-grid">
                  {nextPlayersInfo.players.map((player, index) => (
                    <div key={player.id} className="next-player">
                      <div className="player-name">{player.displayName || player.name}</div>
                      <div className="player-rating">({(player.currentRating || player.rating || 3.5).toFixed(1)})</div>
                    </div>
                  ))}
                </div>
                {nextPlayersInfo.formatted && (
                  <div style={{ 
                    marginTop: '12px', 
                    fontSize: '12px', 
                    color: '#deff01',
                    fontWeight: '500'
                  }}>
                    {nextPlayersInfo.formatted}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                Calculating next players...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bench Section */}
      <div className="sidebar-section">
        <div 
          className="sidebar-header"
          onClick={() => toggleSection('bench')}
          style={{ cursor: 'pointer' }}
        >
          <Users size={20} color="#deff01" />
          <span>Bench ({bench ? bench.length : 0} waiting)</span>
          <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
            {expandedSections.bench ? '▲' : '▼'}
          </span>
        </div>
        
        {expandedSections.bench && (
          <div className="section-content">
            {bench && bench.length > 0 ? (
              <div className="bench-grid">
                {bench.map((player, index) => (
                  <div key={player.id} className="bench-player">
                    <div className="player-name">{player.displayName || player.name}</div>
                    <div className="player-info">
                      <span className="player-rating">({(player.currentRating || player.rating || 3.5).toFixed(1)})</span>
                      {player.gamesUntilEligible > 0 && (
                        <span className="wait-time">Wait: {player.gamesUntilEligible}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                No players on bench
              </div>
            )}
          </div>
        )}
      </div>

      {/* Session Stats */}
      {allPlayers && allPlayers.length > 0 && (
        <div className="sidebar-section">
          <div className="sidebar-header">
            <BarChart3 size={20} color="#deff01" />
            <span>Session Stats</span>
          </div>
          
          <div className="section-content">
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{allPlayers.length}</div>
                <div className="stat-label">Total Players</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">
                  {Math.round(allPlayers.reduce((sum, p) => sum + (p.gamesPlayed || 0), 0) / allPlayers.length * 10) / 10}
                </div>
                <div className="stat-label">Avg Games</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">
                  {competitionLevel === 'no-scoring' ? 'No Tracking' : 'DINKR V2.0'}
                </div>
                <div className="stat-label">Rating System</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{maxPlayers || 24}</div>
                <div className="stat-label">Max Capacity</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;